/**
 * The service corridor as the stealth system sees it: a baked light field and a
 * short list of places that hide her.
 *
 * `src/systems/stealth.ts` deliberately knows nothing about scene graphs, Rapier
 * or meshes. This file is the hand-over for the corridor, so emitters come from
 * the same fixtures the renderer lights with and occluders come from the same
 * solids the physics world is built from. A stealth field authored by hand
 * beside a room authored in code is two rooms that agree until somebody moves a
 * kerb — or, here, opens a bay mouth.
 */

import {
  bakeLightGrid,
  type Aabb,
  type CoverVolume,
  type LightEmitter,
  type LightGrid,
} from '@/systems/stealth';
import { BAYS, CORRIDOR, serviceCorridorFixtures, serviceCorridorSolids } from '@/world/serviceCorridor';

/**
 * The height the field is baked at: her back, not the floor.
 *
 * `bakeLightGrid` defaults to y = 0, which asks whether concrete is lit. The
 * encounter needs to know whether she is lit standing here; this matches
 * `STEALTH_TUNING.targetEyeHeight`, so the baked point and a watcher's target
 * point are the same point.
 */
const SAMPLE_Y = 0.2;

/** Anything shorter than this cannot shadow a body at `SAMPLE_Y`. */
const OCCLUDER_MIN_TOP = SAMPLE_Y + 0.05;

/**
 * Solids that are architecture rather than obstruction.
 *
 * Floors lie below the sample plane and ceilings above every emitter. Including
 * either adds rays that can only say no; the door leaves stay because closed is
 * the room's authored starting state, not a presentation detail.
 */
const NOT_AN_OCCLUDER = ['floor-', 'ceiling'] as const;

function solidToAabb(half: { x: number; y: number; z: number }, pos: { x: number; y: number; z: number }): Aabb {
  return {
    min: { x: pos.x - half.x, y: pos.y - half.y, z: pos.z - half.z },
    max: { x: pos.x + half.x, y: pos.y + half.y, z: pos.z + half.z },
  };
}

/**
 * What blocks light in the corridor, from the list the room is actually built from.
 *
 * Matched by name PREFIX rather than exact name deliberately. A long wall becomes
 * several named solids whenever a new bay cuts a mouth into it; an exact-name
 * filter would silently stop shadowing the replacement pieces on that edit.
 */
export function serviceOccluders(): Aabb[] {
  const occluders: Aabb[] = [];
  for (const solid of serviceCorridorSolids()) {
    if (NOT_AN_OCCLUDER.some((prefix) => solid.name.startsWith(prefix))) continue;
    const box = solidToAabb(solid.half, solid.pos);
    if (box.max.y < OCCLUDER_MIN_TOP) continue;
    occluders.push(box);
  }
  return occluders;
}

/**
 * The room's lights, as the stealth system understands light.
 *
 * Renderer candela is deliberately not carried through. The field is a 0..1
 * exposure term in a visibility product, so every practical is normalised to a
 * lit corridor fixture and the bake wrapper below clamps their deliberate overlap.
 */
export function serviceEmitters(): LightEmitter[] {
  return serviceCorridorFixtures().map((fixture) => ({
    kind: 'point' as const,
    position: { ...fixture.position },
    radius: fixture.range,
    intensity: 1,
  }));
}

/**
 * Where she is hidden, and by how much.
 *
 * Five volumes and no more. The bays are high concealment because there is
 * nothing else in the room; the two pipe spans are only a slightly safer crossing
 * lane. A player who cannot hold this list in her head learns nothing about when
 * to move, which is the entire stealth lesson this room is here to teach.
 */
export function serviceCover(): CoverVolume[] {
  const cover: CoverVolume[] = BAYS.map((bay) => ({
    min: { x: bay.minX, y: 0, z: bay.minZ },
    max: { x: bay.maxX, y: 1.0, z: bay.maxZ },
    concealment: 0.8,
  }));

  cover.push(
    {
      min: { x: 0.75, y: 0, z: CORRIDOR.longMinZ },
      max: { x: CORRIDOR.longMaxX, y: 0.5, z: BAYS[1]!.minZ },
      concealment: 0.3,
    },
    {
      min: { x: 0.75, y: 0, z: BAYS[1]!.maxZ },
      max: { x: CORRIDOR.longMaxX, y: 0.5, z: CORRIDOR.shortMinZ },
      concealment: 0.3,
    },
    // The short leg's run, and it is the only cover in that leg at all. Beat 4's
    // last stage is three seconds of an open door, and this is the lane she is
    // allowed to already be in when it opens. Same weak 0.3 as the long leg's:
    // it is not a hiding place, it is a better place to be than the middle.
    {
      min: { x: CORRIDOR.longMaxX, y: 0, z: CORRIDOR.shortMinZ },
      max: { x: CORRIDOR.pipeShortLegMaxX, y: 0.5, z: 8.25 },
      concealment: 0.3,
    },
  );
  return cover;
}

/**
 * Bake the corridor's light field. Called once at zone load; sampling it is O(1).
 *
 * `bakeLightGrid` correctly measures the sum of overlapping fixtures and does
 * not clamp. The visibility system expects its light term in 0..1, though, so
 * clamp at this seam rather than turning the deliberately overlapping renderer
 * fixtures into a second, weaker lighting design.
 */
export function bakeServiceLightGrid(): LightGrid {
  const grid = bakeLightGrid(
    {
      minX: -3.4,
      minZ: -9.2,
      width: 15.6,
      depth: 19.6,
      sampleY: SAMPLE_Y,
    },
    serviceEmitters(),
    serviceOccluders(),
  );

  return {
    ...grid,
    sample: (x: number, z: number): number => Math.min(1, grid.sample(x, z)),
  };
}
